import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FilterSelect,
  ListToolbar,
  Pager,
  SearchBox,
  useListView,
} from '../../components/ListControls.js';
import { type AdminUserRow, type CustomerRow, type CustomerUserRow, api } from '../../lib/api.js';

export function Users() {
  const [tab, setTab] = useState<'customer' | 'admin'>('customer');
  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">사용자 관리</h1>
      </header>
      <div className="mb-4 border-b border-neutral-200">
        <nav className="-mb-px flex gap-4">
          <TabBtn active={tab === 'customer'} onClick={() => setTab('customer')}>
            고객사 사용자
          </TabBtn>
          <TabBtn active={tab === 'admin'} onClick={() => setTab('admin')}>
            관리자
          </TabBtn>
        </nav>
      </div>
      {tab === 'customer' ? <CustomerUsersTab /> : <AdminUsersTab />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`pb-2 px-1 text-sm border-b-2 transition-colors ${
        active
          ? 'border-brand-500 text-brand-700 font-medium'
          : 'border-transparent text-neutral-600 hover:text-neutral-900'
      }`}
    >
      {children}
    </button>
  );
}

export function CustomerUsersTab({ fixedCustomerId }: { fixedCustomerId?: string }) {
  const [users, setUsers] = useState<CustomerUserRow[]>([]);
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    customerId: fixedCustomerId ?? '',
    email: '',
    displayName: '',
    role: 'member' as 'owner' | 'member',
  });
  const [tempCred, setTempCred] = useState<{ email: string; tempPassword: string } | null>(null);
  // 검색(이메일·이름·고객사) + 상태 필터
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'disabled'>('all');
  const list = useListView(
    users.filter(
      (u) =>
        statusFilter === 'all' || (statusFilter === 'disabled' ? !!u.disabledAt : !u.disabledAt),
    ),
    (u) =>
      [u.email, u.displayName, u.customerName ?? '', u.role === 'owner' ? 'owner' : 'member'].join(
        ' ',
      ),
  );

  async function reload() {
    try {
      const qs = fixedCustomerId
        ? `?role=customer&customerId=${fixedCustomerId}`
        : `?role=customer`;
      const [u, c] = await Promise.all([
        api.get<{ users: CustomerUserRow[] }>(`/api/admin/users${qs}`),
        fixedCustomerId
          ? Promise.resolve({ customers: [] as CustomerRow[] })
          : api.get<{ customers: CustomerRow[] }>('/api/admin/customers'),
      ]);
      setUsers(u.users);
      setCustomers(c.customers);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedCustomerId]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await api.post<{ user: CustomerUserRow; tempPassword: string }>(
        '/api/admin/users',
        {
          kind: 'customer',
          data: {
            customerId: form.customerId,
            email: form.email,
            displayName: form.displayName,
            role: form.role,
          },
        },
      );
      setTempCred({ email: res.user.email, tempPassword: res.tempPassword });
      setCreating(false);
      setForm({
        customerId: fixedCustomerId ?? '',
        email: '',
        displayName: '',
        role: 'member',
      });
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function reset(id: string) {
    if (!confirm('비밀번호를 임시 비밀번호로 초기화하시겠습니까?')) return;
    try {
      const res = await api.post<{ tempPassword: string }>(
        `/api/admin/users/${id}/reset-password`,
        {},
      );
      const user = users.find((u) => u.id === id);
      setTempCred({ email: user?.email ?? '?', tempPassword: res.tempPassword });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function toggleDisabled(u: CustomerUserRow) {
    try {
      await api.patch(`/api/admin/users/${u.id}`, { disabled: !u.disabledAt });
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="px-3 py-1.5 bg-brand-400 hover:bg-brand-600 text-white text-sm font-medium rounded-md"
        >
          {creating ? '취소' : '+ 새 사용자'}
        </button>
      </div>

      {creating && (
        <form
          onSubmit={create}
          className="mb-4 p-5 bg-surface border border-neutral-200 rounded-lg space-y-3"
        >
          {!fixedCustomerId && (
            <label className="block">
              <span className="block text-xs font-medium text-neutral-700 mb-1">고객사 *</span>
              <select
                value={form.customerId}
                onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                required
                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
              >
                <option value="">선택…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">이메일 *</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">이름 *</span>
            <input
              type="text"
              required
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">역할</span>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as 'owner' | 'member' })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              <option value="member">Member</option>
              <option value="owner">Owner</option>
            </select>
          </label>
          <button
            type="submit"
            className="px-4 py-2 bg-brand-400 hover:bg-brand-600 text-white text-sm font-medium rounded-md"
          >
            저장
          </button>
        </form>
      )}

      {tempCred && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-900 font-medium">임시 비밀번호 발급</p>
          <p className="mt-1 text-sm text-amber-800">
            <strong>{tempCred.email}</strong> 에게 안전한 채널로 전달하세요. 이 화면을 떠나면 다시
            볼 수 없습니다.
          </p>
          <div className="mt-2 px-3 py-2 bg-surface border border-amber-300 rounded font-mono text-sm">
            {tempCred.tempPassword}
          </div>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(tempCred.tempPassword);
            }}
            className="mt-2 px-3 py-1 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 text-xs rounded mr-2"
          >
            복사
          </button>
          <button
            type="button"
            onClick={() => setTempCred(null)}
            className="px-3 py-1 text-neutral-600 hover:text-neutral-900 text-xs"
          >
            닫기
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <ListToolbar
        total={list.total}
        unit="명"
        searching={!!list.query.trim()}
        size={list.size}
        onSize={list.setSize}
      >
        <SearchBox
          value={list.query}
          onChange={list.setQuery}
          placeholder={fixedCustomerId ? '이메일·이름 검색' : '이메일·이름·고객사 검색'}
        />
        <FilterSelect
          label="상태"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            list.resetPage();
          }}
          options={[
            { value: 'all', label: '상태: 전체' },
            { value: 'active', label: 'active' },
            { value: 'disabled', label: 'disabled' },
          ]}
        />
      </ListToolbar>

      <div className="bg-surface border border-neutral-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
            <tr>
              {!fixedCustomerId && <th className="text-left px-4 py-2 font-medium">고객사</th>}
              <th className="text-left px-4 py-2 font-medium">이메일</th>
              <th className="text-left px-4 py-2 font-medium">이름</th>
              <th className="text-left px-4 py-2 font-medium">역할</th>
              <th className="text-left px-4 py-2 font-medium">상태</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {list.pageItems.map((u) => (
              <tr key={u.id} className="hover:bg-neutral-50/50">
                {!fixedCustomerId && (
                  <td className="px-4 py-2 text-neutral-700">
                    {u.customerName ? (
                      <Link
                        to={`/admin/customers/${u.customerId}`}
                        className="hover:text-brand-600"
                      >
                        {u.customerName}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </td>
                )}
                <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-2">{u.displayName}</td>
                <td className="px-4 py-2 text-neutral-600">
                  {u.role === 'owner' ? 'Owner' : 'Member'}
                </td>
                <td className="px-4 py-2">
                  {u.disabledAt ? (
                    <span className="text-neutral-500 text-xs">disabled</span>
                  ) : (
                    <span className="text-green-700 text-xs font-medium">active</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-xs space-x-3">
                  <button
                    type="button"
                    onClick={() => reset(u.id)}
                    className="text-neutral-600 hover:text-neutral-900"
                  >
                    비밀번호 재설정
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleDisabled(u)}
                    className="text-neutral-600 hover:text-neutral-900"
                  >
                    {u.disabledAt ? '활성화' : '비활성화'}
                  </button>
                </td>
              </tr>
            ))}
            {list.total === 0 && (
              <tr>
                <td
                  colSpan={fixedCustomerId ? 5 : 6}
                  className="px-4 py-8 text-center text-neutral-500"
                >
                  {users.length === 0 ? '사용자가 없습니다.' : '조건에 맞는 사용자가 없습니다.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager page={list.page} pages={list.pages} onPage={list.setPage} />
    </div>
  );
}

function AdminUsersTab() {
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', displayName: '', password: '' });

  async function reload() {
    try {
      const u = await api.get<{ users: AdminUserRow[] }>('/api/admin/users?role=admin');
      setUsers(u.users);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    reload();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await api.post('/api/admin/users', {
        kind: 'admin',
        data: form,
      });
      setCreating(false);
      setForm({ email: '', displayName: '', password: '' });
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          className="px-3 py-1.5 bg-brand-400 hover:bg-brand-600 text-white text-sm font-medium rounded-md"
        >
          {creating ? '취소' : '+ 새 관리자'}
        </button>
      </div>
      {creating && (
        <form
          onSubmit={create}
          className="mb-4 p-5 bg-surface border border-neutral-200 rounded-lg space-y-3"
        >
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">이메일 *</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">이름 *</span>
            <input
              type="text"
              required
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-neutral-700 mb-1">
              초기 비밀번호 (12자 이상)
            </span>
            <input
              type="password"
              required
              minLength={12}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>
          <button
            type="submit"
            className="px-4 py-2 bg-brand-400 hover:bg-brand-600 text-white text-sm font-medium rounded-md"
          >
            저장
          </button>
        </form>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-surface border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2 font-medium">이메일</th>
              <th className="text-left px-4 py-2 font-medium">이름</th>
              <th className="text-left px-4 py-2 font-medium">최근 로그인</th>
              <th className="text-left px-4 py-2 font-medium">생성일</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {users.map((u) => (
              <tr key={u.id}>
                <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-2">{u.displayName}</td>
                <td className="px-4 py-2 text-neutral-500 text-xs">
                  {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('ko-KR') : '—'}
                </td>
                <td className="px-4 py-2 text-neutral-500 text-xs">
                  {new Date(u.createdAt).toLocaleDateString('ko-KR')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
